/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 대량의 데이터를 메인쓰레드 잠김없이 처리하기 위한 클래스를 정의한다.
 *
 * @file /src/utils/Chunks.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 29.
 */
import { AnimationFrames } from '@utils/AnimationFrames';
export interface ChunksOptions {
    frameBudget?: number;
    onProgress?: (done: number, total: number) => void;
}

export class Chunks<T, R = any> {
    private data: T[];
    private results: R[];
    private processed = 0;
    private total = 0;
    private rAFId: string = null;
    private resolver!: (value: any) => void;
    private rejecter?: (reason?: unknown) => void;
    private readonly budget: number;

    /**
     * 청크 클래스를 정의한다.
     *
     * @param data - 처리할 데이터 배열
     * @param options - 청크옵션
     */
    constructor(data: T[], private readonly options: ChunksOptions = {}) {
        this.data = data.slice();
        this.results = [];
        this.budget = options.frameBudget ?? 16;
    }

    /**
     * 데이터 전체 갯수를 가져온다.
     *
     * @returns 데이터 갯수
     */
    getTotal(): number {
        return this.data.length;
    }

    /**
     * 데이터를 처리한다.
     * 프레임예산내에 처리할 수 있는만큼 처리하고, 예산범위를 벗어날 경우 다음 프레임에서 이어서 처리한다.
     *
     * @param transform - 데이터처리 콜백함수
     * @param label - 라벨
     */
    async execute(
        transform: (origin: T, index: number, total: number) => R | void | Promise<R | void>,
        label: string = null
    ): Promise<R[]> {
        if (this.rAFId !== null) {
            AnimationFrames.cancel(this.rAFId, true);
        }

        const data = this.data;

        this.processed = 0;
        this.total = data.length;
        this.results = [];
        this._currentTransform = transform;

        return new Promise<R[]>((resolve, reject) => {
            this.resolver = resolve;
            this.rejecter = reject;
            this.data = data;
            this.rAFId = AnimationFrames.request(this.tick, label ?? 'Chunks execute', this);
        });
    }

    /**
     * 프레임당 처리할 마이크로 함수를 정의한다.
     */
    private tick(frameStart: DOMHighResTimeStamp): boolean {
        const deadline = frameStart + this.budget;

        const processNext = async () => {
            try {
                while (this._currentTransform !== undefined && this.data.length && performance.now() < deadline) {
                    const origin = this.data.shift() as T;
                    const maybeResult = await this._currentTransform!(origin, this.processed, this.total);
                    if (maybeResult !== undefined) {
                        this.results.push(maybeResult as R);
                    }
                    this.processed++;
                    this.options.onProgress?.(this.processed, this.total);
                }
            } catch (e) {
                this.rejecter(e);
                this.#reset();
                return;
            }

            if (this.data.length > 0) {
                AnimationFrames.loop(this.rAFId);
            } else {
                AnimationFrames.cancel(this.rAFId, true);
                this.rAFId = null;
                this._currentTransform = undefined;
                if (this.resolver !== undefined) {
                    this.resolver(this.results);
                }
            }
        };

        processNext();
        return false;
    }

    /**
     * 대량의 데이터를 머지소팅 방식으로 정렬한다.
     * 데이터의 수가 적을 경우 퀵소팅 방식으로 1프레임내에 처리한다.
     *
     * @param comparator - 데이터비교 콜백
     */
    async sort(comparator: (left: T, right: T) => number): Promise<T[]> {
        if (this.rAFId !== null) {
            AnimationFrames.cancel(this.rAFId, true);
        }

        const data = this.data;

        /**
         * 데이터수가 적을 경우 퀵소트로 하나의 프레임 내에서 처리한다.
         */
        if (data.length <= 100) {
            return new Promise<T[]>((resolve) => {
                this.rAFId = AnimationFrames.request(() => {
                    data.sort(comparator);
                    resolve(data);
                    AnimationFrames.cancel(this.rAFId, true);
                    this.rAFId = null;

                    return false;
                }, 'Chunks sort (hybrid)');
            });
        }

        /**
         * merge sort 상태 준비
         */
        this._sortState = {
            segments: [],
            done: false,
            comparator,
            step: () => {
                const segments = this._sortState!.segments;

                if (segments.length <= 1) {
                    this._sortState!.done = true;
                    return;
                }

                const newSegments: T[][] = [];

                for (let i = 0; i < segments.length; i += 2) {
                    if (i + 1 < segments.length) {
                        const merged = this._merge(segments[i], segments[i + 1], this._sortState!.comparator);
                        newSegments.push(merged);
                    } else {
                        newSegments.push(segments[i]);
                    }
                }

                this._sortState!.segments = newSegments;
            },
        };

        await new Chunks<T>(this.data).execute(async (item) => {
            this._sortState!.segments.push([item]);
        });

        return new Promise<T[]>((resolve, reject) => {
            this.resolver = resolve;
            this.rejecter = reject;
            this.rAFId = AnimationFrames.request(this.tickSort, 'Chunks sort', this);
        });
    }

    /**
     * 프레임당 정렬할 마이크로함수를 정의한다.
     */
    private tickSort(frameStart: DOMHighResTimeStamp): boolean {
        /**
         * 분할된 데이터양에 따라 프레임예산을 조절한다.
         */
        const segmentsLeft = this._sortState!.segments.length;

        let dynamicBudget = 2;
        if (segmentsLeft > 1000) {
            dynamicBudget = 2;
        } else if (segmentsLeft > 500) {
            dynamicBudget = 4;
        } else if (segmentsLeft > 100) {
            dynamicBudget = 6;
        } else {
            dynamicBudget = 8;
        }

        const deadline = frameStart + dynamicBudget;

        try {
            while (!this._sortState!.done && performance.now() < deadline) {
                this._sortState!.step();
                this.options.onProgress?.(this.total - this._sortState!.segments.length, this.total);
            }
        } catch (e) {
            this.rejecter(e);
            this.#reset();
            return false;
        }

        if (!this._sortState!.done) {
            AnimationFrames.loop(this.rAFId);
        } else {
            AnimationFrames.cancel(this.rAFId, true);
            this.rAFId = null;
            this.resolver(this._sortState!.segments[0]);
            this._sortState = undefined;
        }

        return false;
    }

    /**
     * 분할하여 정렬된 데이터를 병합한다.
     */
    private _merge(a: T[], b: T[], comparator: (a: T, b: T) => number): T[] {
        const result: T[] = [];
        let i = 0,
            j = 0;

        while (i < a.length && j < b.length) {
            if (comparator(a[i], b[j]) <= 0) {
                result.push(a[i++]);
            } else {
                result.push(b[j++]);
            }
        }

        while (i < a.length) result.push(a[i++]);
        while (j < b.length) result.push(b[j++]);

        return result;
    }

    /**
     * 현재 수행중인 데이터처리작업을 중단한다.
     */
    cancel(): void {
        if (this.rAFId !== null && AnimationFrames.isActive(this.rAFId) == true) {
            AnimationFrames.cancel(this.rAFId, true);
            this.rAFId = null;
        }

        this.resolver(this.results);

        this.#reset();
    }

    /**
     * 내부데이터를 초기화한다.
     */
    #reset(): void {
        if (this.rAFId !== null && AnimationFrames.isActive(this.rAFId) == true) {
            AnimationFrames.cancel(this.rAFId, true);
            this.rAFId = null;
        }

        this.rejecter = undefined;
        this._currentTransform = undefined;
        this._sortState = undefined;
        this.resolver = undefined;
        this.results = undefined;
    }

    // 내부 상태
    private _currentTransform?: (origin: T, index: number, total: number) => R | void | Promise<R | void>;
    private _sortState?: {
        segments: T[][];
        done: boolean;
        comparator: (a: T, b: T) => number;
        step: () => void;
    };
}
